import React, { useState } from "react";
import moment from "moment-timezone";
import { useDispatch } from "react-redux";
import { fetchUserInfo, fetchUserLogin } from "@/stores/slices/authSlice";
import { activeUserProfile, sendOTPVerifyEmail, sendOTPVerifyPhone, signUp } from "@/stores/apis/apiAuth";
import { toast } from "@/modules/toast";
import { NOTIFICATION_TYPE } from "@/config/constants";
import ConfirmOTP from "./ConfirmOTP";
import InputProfile from "./InputProfile";
import RegisterAccountSendOTP from "./SendOTP";
import { OTP_TIMEOUT } from "@/config/constants";
import { noop } from "lodash";

const RegisterComponent = ({ goBack, onRegisterSuccess, onFinish = noop }) => {
	const dispatch = useDispatch();
	const [step, setStep] = useState("INPUT_PROFILE"); // INPUT_PROFILE --> SEND_OTP -> VERIFICATION_OTP
	const [expiredDate, setExpiredDate] = useState("");
	const [payload, setPayload] = useState({});
	const [userInfo, setUserInfo] = useState({});
	const [methodVerify, setMethodVerify] = useState();
	const [loading, setLoading] = useState(false);

	const nextStep = (route) => {
		setStep(route);
	};

	const onGoBack = () => {
		goBack && goBack();
	};

	const onSubmitConfirmOTP = async (otp) => {
		if (loading) return;
		setLoading(true);

		const data = {
			code: otp,
			method: methodVerify === "phone" ? "sms" : "email",
			email: userInfo?.email,
		};
		const rs = await activeUserProfile(data);
		if (rs && rs.success) {
			onFinish();
			dispatch(fetchUserInfo());
			setStep("INPUT_PROFILE");
			onRegisterSuccess?.();

			toast({
				type: NOTIFICATION_TYPE.SUCCESS,
				description: "Your Account Has Been Verified Successfully.",
			});
		} else {
			toast({
				type: NOTIFICATION_TYPE.ERROR,
				description: "Verification Failed.",
			});
		}

		setTimeout(() => {
			setLoading(false);
		}, 200);
	};

	const onSendOTP = async (type) => {
		if (loading) return;
		setLoading(true);

		setMethodVerify(type);

		const payload = {
			email: userInfo?.email,
			phone: userInfo?.phone,
		};

		let result;
		if (type === "email") {
			result = await sendOTPVerifyEmail(payload);
		} else {
			result = await sendOTPVerifyPhone(payload);
		}
		if (result && result.success) {
			nextStep("VERIFICATION_OTP");
			setExpiredDate(moment().add(OTP_TIMEOUT, "minutes").toString());
		} else {
			toast({
				type: NOTIFICATION_TYPE.ERROR,
				description: result?.error?.message || "Send OTP Failed.",
			});
		}

		setTimeout(() => {
			setLoading(false);
		}, 200);
	};

	const onResendOTP = async () => {
		if (loading) return;
		setLoading(true);

		const payload = {
			email: userInfo?.email,
			phone: userInfo?.phone,
		};

		let result;
		if (methodVerify === "email") {
			result = await sendOTPVerifyEmail(payload);
		} else {
			result = await sendOTPVerifyPhone(payload);
		}
		if (result && result.success) {
			setExpiredDate(moment().add(OTP_TIMEOUT, "minutes").toString());
			toast({
				type: NOTIFICATION_TYPE.SUCCESS,
				description: "OTP Resent Successfully.",
			});
		} else {
			toast({
				type: NOTIFICATION_TYPE.ERROR,
				description: "Send OTP Failed.",
			});
		}

		setTimeout(() => {
			setLoading(false);
		}, 200);
	};

	const onPressSignUp = async (values) => {
		let payload = {
			user_data: {
				address: values?.address,
				email: values?.email,
				first_name: values?.first_name,
				last_name: values?.last_name,
				phone: values?.phone,
				password: values?.password,
				settings: {
					time_format: "TWELVE_HOURS_FORMAT",
				},
			},
		};

		if (loading) return;
		setLoading(true);

		setUserInfo(values);
		let result = await signUp(payload);
		if (result && result.success) {
			dispatch(
				fetchUserLogin({
					email: values?.email,
					password: values?.password,
				}),
			);

			nextStep("SEND_OTP");
			setPayload({ ...payload });
		} else {
			toast({
				type: NOTIFICATION_TYPE.ERROR,
				description: result?.errors?.message ?? "Registration Failed.",
			});
		}

		// close loading
		setTimeout(() => {
			setLoading(false);
		}, 200);
	};

	const _renderRegiterWithProcess = () => {
		switch (step) {
			case "INPUT_PROFILE":
				return <InputProfile onPressLogin={onGoBack} onPressSignUp={onPressSignUp} loading={loading} />;
			case "SEND_OTP":
				return (
					<RegisterAccountSendOTP
						userInfo={userInfo}
						onSendOTP={onSendOTP}
						onPressLogin={onGoBack}
						loading={loading}
					/>
				);
			case "VERIFICATION_OTP":
				return (
					<ConfirmOTP
						payload={payload}
						method={methodVerify}
						expiredDate={expiredDate}
						onSubmitConfirmOTP={onSubmitConfirmOTP}
						onBack={() => setStep("SEND_OTP")}
						onResendOTP={onResendOTP}
						loading={loading}
					/>
				);

			default:
				return null;
		}
	};

	return _renderRegiterWithProcess();
};

export default RegisterComponent;
